import { ResizeObserver } from '@juggle/resize-observer';
import * as LocalStorage from '@purple-dot/browser-sdk/src/libraries/local-storage';
import * as SessionStorage from '@purple-dot/browser-sdk/src/libraries/session-storage';
import useEventListener from '@use-it/event-listener';
import React, { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { createGlobalStyle } from 'styled-components';
import { AlertIcon } from '../../components/EmbeddedApp/CartButton/AlertIcon';
import { Button } from '../../components/EmbeddedApp/CartButton/Button';
import { ClockIcon } from '../../components/EmbeddedApp/CartButton/ClockIcon';
import * as CheckoutApi from '../../components/EmbeddedApp/checkout-api';
import IframePage from '../../components/EmbeddedApp/PageTypes/IframePage';
import expressPage from '../../webserver-page';

function CartButtonIframe({
  secureSessionToken,
  showButton,
  checkoutId,
  instanceId,
  style: initialStyle,
}) {
  const [style, setStyling] = useState(initialStyle);

  const parentPage = CartParentPage({ instanceId });

  useEffect(() => {
    parentPage.loaded(showButton);

    const elem = document.body;
    const resizeObserver = new ResizeObserver(() => {
      parentPage.sizeChanged(elem.offsetWidth, elem.offsetHeight);
    });
    resizeObserver.observe(elem);
  }, []);

  useEventListener('message', (event) => {
    const message = event.data;

    if (message?.type && message?.data) {
      const { type, data } = message;

      if (type === 'UPDATE') {
        setStyling({
          ...style,
          fill: data.fill,
        });
      }
    }
  });

  useEventListener('storage', () => {
    const checkoutSession = SessionStorage.getItem('checkoutSession');
    const show = checkoutSession && checkoutSession.lineItems.length > 0;
    parentPage.postVisibilityChanged({ show });
  });

  useSecureTokenEffect({ secureSessionToken });
  useFetchCheckoutEffect({ checkoutId });

  return (
    <IframePage head={{}} useFullstory={false}>
      <NoBodyBackground
        globalCSS={`html,body { height: ${style.height}; width: ${style.width}; }`}
      />
      <Button
        styling={style}
        onMouseEnter={() => {
          // Check if we have a mouse and dont fire if we dont
          if (!window.matchMedia('(pointer:fine)').matches) {
            return;
          }

          parentPage.cartButtonEntered();
        }}
        onClick={() => {
          parentPage.cartButtonClicked();
        }}
      >
        <ClockIcon />
        <AlertIcon data-testid="alert-icon" styling={style} />
      </Button>
    </IframePage>
  );
}

const NoBodyBackground = createGlobalStyle`
  body {
    background: none transparent;
  }

  ${({ globalCSS }) => globalCSS || ''}
`;

function useSecureTokenEffect({ secureSessionToken }) {
  if (secureSessionToken) {
    LocalStorage.setItem('_pd_secure_session_token', secureSessionToken);
  }
}

/*
  Fetch and save the checkout session as soon as possible
  so the other components can use it
*/
function useFetchCheckoutEffect({ checkoutId }) {
  const FetchCheckoutTask = useAsync({
    deferFn: CheckoutApi.fetchCheckout,
    onResolve: (data) => {
      SessionStorage.setItem('checkoutSession', data);
    },
    onReject: (err) => {
      // biome-ignore lint/suspicious/noConsole: log
      console.error(
        `PurpleDot: Failed to fetch checkout for ${checkoutId}`,
        err
      );
    },
  });

  useEffect(() => {
    if (checkoutId) {
      SessionStorage.setItem('serverCheckoutId', checkoutId);
      FetchCheckoutTask.run({ checkoutId });
    } else {
      SessionStorage.removeItem('checkoutSession');
      SessionStorage.removeItem('serverCheckoutId');
    }
  }, []);
}

function CartParentPage({ instanceId }) {
  if (!instanceId) {
    throw new Error('No instanceId provided');
  }

  function sendMessage(type, data) {
    window.parent.postMessage(
      {
        meta: {
          placementType: 'cart-button',
          instanceId,
          type,
        },
        data: data || {},
      },
      '*'
    );
  }

  return {
    loaded(showButton) {
      sendMessage('loaded', { showButton });
    },

    sizeChanged(width, height) {
      sendMessage('window-size-changed', { width, height });
    },

    postVisibilityChanged({ show }) {
      sendMessage('visibility-changed', { show });
    },

    cartButtonEntered() {
      sendMessage('cart-button-entered');
    },

    cartButtonClicked() {
      sendMessage('cart-button-clicked');
    },
  };
}

export default expressPage(CartButtonIframe);
